import { api, loginUrl } from '../api/endpoints'
import { ApiError, isNetworkError, setUnauthorizedHandler } from '../api/client'
import { handleUnauthorized } from '../auth/authRedirect'
import { clearLocalData, db } from '../db/db'
import { getCachedMe, setCachedMe } from '../db/meta'
import { configureHub, reconnectNow, STALE_AFTER_HIDDEN_MS, startHub, stopHub } from '../realtime/hub'
import { registerHandlers } from '../realtime/handlers'
import { restoreConnectionState } from '../realtime/subscriptions'
import { useConnection } from '../state/connection'
import { useEphemeral } from '../state/ephemeral'
import { useSession } from '../state/session'
import { kickOutbox, resetStuckOutbox, retryAllNow } from '../sync/outbox'
import { syncAll } from '../sync/syncEngine'
import { refreshPushSubscription } from '../push/push'
import { isNative } from '../native/native'
import { startNativePush } from '../native/nativePush'
import { configureDeviceGate, onDeviceEvent, onDeviceNotApproved, onVisibleWhileGated, resetDeviceGate, runDeviceCheck } from '../device/gate'
import { deviceAllowed, setDeviceRecheckHandler, useDeviceGate } from '../state/device'
import { bumpAdminDevices } from '../state/adminDevices'

const PERIODIC_SYNC_MS = 5 * 60 * 1000

let started = false
let periodic: ReturnType<typeof setInterval> | null = null

async function loadPresence(): Promise<void> {
  try {
    useEphemeral.getState().setOnlineUsers(await api.presence())
  } catch {
    // presence is best effort
  }
}

async function onHubConnected(): Promise<void> {
  if (!deviceAllowed()) {
    void stopHub()
    return
  }
  restoreConnectionState()
  void loadPresence()
  kickOutbox()
  await syncAll()
}

/**
 * GET /api/me; returns true when the user is authenticated. `beforeAttest` marks an enforce-mode
 * session that is about to attest, so the gate shows a spinner rather than flashing the waiting screen.
 */
export async function refreshMe(options: { beforeAttest?: boolean } = {}): Promise<boolean> {
  try {
    const me = await api.me()
    const cached = await getCachedMe()
    if (cached && cached.id !== me.id) await clearLocalData()
    await setCachedMe(me)
    await db.users.put(me)
    if (options.beforeAttest && me.devicePolicy === 'enforce' && me.device?.status === 'unbound') {
      useDeviceGate.getState().setAttesting(true)
    }
    useDeviceGate.getState().setFromMe(me)
    useSession.getState().setMe(me)
    return true
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return false
    if (!isNetworkError(error)) console.warn('GET /api/me failed', error)
    // Offline or server down: keep the cached identity if there is one.
    if (!useSession.getState().me) useSession.getState().setUnauthenticated()
    return Boolean(useSession.getState().me)
  }
}

async function connectAndSync(): Promise<void> {
  if (!deviceAllowed()) return
  await startHub()
  if (!deviceAllowed()) return
  await syncAll()
}

/** Everything after identity and device checks: push, hub, sync. */
async function continueStartup(): Promise<void> {
  if (!useSession.getState().me || !deviceAllowed()) return
  if (isNative()) void startNativePush()
  else void refreshPushSubscription(useSession.getState().me?.vapidPublicKey ?? null)
  await connectAndSync()
}

let signedInRun: Promise<void> | null = null

/** GET /api/me → device attestation → (gate, or) hub and sync. */
export function signedInFlow(): Promise<void> {
  if (signedInRun) return signedInRun
  signedInRun = (async () => {
    const ok = await refreshMe({ beforeAttest: true })
    if (!ok || !useSession.getState().me) {
      useDeviceGate.getState().setAttesting(false)
      return
    }
    // In enforce mode an unapproved device stops here: the gate polls and resumes start-up.
    if (!(await runDeviceCheck())) return
    await continueStartup()
  })().finally(() => {
    signedInRun = null
  })
  return signedInRun
}

/** Clears this installation's chat data (sign-out). The device key itself is kept. */
export async function clearDeviceState(): Promise<void> {
  resetDeviceGate()
  await stopHub()
  await clearLocalData()
  useEphemeral.getState().reset()
  useSession.getState().reset()
  try {
    if ('caches' in window) {
      const keys = await caches.keys()
      // Keep the precached app shell so the sign-in screen still loads offline.
      await Promise.all(keys.filter((k) => !k.includes('precache')).map((k) => caches.delete(k)))
    }
  } catch {
    // ignore
  }
}

async function onSessionRevoked(): Promise<void> {
  await clearDeviceState()
  if (isNative()) useSession.getState().setUnauthenticated()
  else window.location.assign(loginUrl('/'))
}

let hiddenSince: number | null = null

function onVisible(): void {
  if (document.visibilityState === 'hidden') {
    hiddenSince = Date.now()
    return
  }
  if (document.visibilityState !== 'visible' || !useSession.getState().me) return
  if (!deviceAllowed()) {
    onVisibleWhileGated()
    return
  }
  const away = hiddenSince === null ? 0 : Date.now() - hiddenSince
  hiddenSince = null
  void startHub()
  void reconnectNow(away < STALE_AFTER_HIDDEN_MS)
  void syncAll()
}

/** App start: cached data first, then /api/me, then the hub, then sync. */
export async function startApp(): Promise<void> {
  if (started) return
  started = true

  setUnauthorizedHandler(handleUnauthorized)
  setDeviceRecheckHandler(onDeviceNotApproved)
  configureDeviceGate({
    onAllowed: () => void continueStartup(),
    onGated: () => void stopHub(),
  })
  configureHub({
    register: (connection) =>
      registerHandlers(connection, {
        onSessionRevoked: () => void onSessionRevoked(),
        onSettingsChanged: () => void refreshMe(),
        onDeviceEvent: (event) => {
          // Pending count for the admin badge, and any open admin devices list.
          void refreshMe()
          bumpAdminDevices()
          void onDeviceEvent(event.type, event.deviceId)
        },
      }),
    onConnected: () => void onHubConnected(),
  })

  const cached = await getCachedMe()
  if (cached) {
    // The gate decides from the cached device status too, so an unapproved device never shows cached chats.
    useDeviceGate.getState().setFromMe(cached)
    useSession.getState().setMe(cached, false)
  }
  await resetStuckOutbox()

  window.addEventListener('online', () => {
    useConnection.getState().setOnline(true)
    if (useSession.getState().authStatus === 'unauthenticated' || !useSession.getState().me) {
      void signedInFlow()
      return
    }
    if (!deviceAllowed()) {
      void signedInFlow()
      return
    }
    void retryAllNow()
    void startHub()
    // The network changed: the old socket is on the old route, whatever it says.
    void reconnectNow(false)
    void syncAll()
  })
  window.addEventListener('offline', () => useConnection.getState().setOnline(false))
  document.addEventListener('visibilitychange', onVisible)

  periodic = setInterval(() => {
    if (document.visibilityState === 'visible' && useSession.getState().me && deviceAllowed()) void syncAll()
  }, PERIODIC_SYNC_MS)

  if (!navigator.onLine) {
    if (!cached) useSession.getState().setUnauthenticated()
    return
  }
  await signedInFlow()
}

export function stopPeriodicSync(): void {
  if (periodic) clearInterval(periodic)
  periodic = null
}
